import React, { FC } from 'react';
import { Box, styled } from '@mui/material';

interface TimeSeriesDataPoint {
  point: string;
  value: number;
}

interface TimeSeriesChartProps {
  historicData: TimeSeriesDataPoint[];
  forecastData: TimeSeriesDataPoint[];
  lowerConfidenceLimits80: TimeSeriesDataPoint[];
  upperConfidenceLimits80: TimeSeriesDataPoint[];
  lowerConfidenceLimits95: TimeSeriesDataPoint[];
  upperConfidenceLimits95: TimeSeriesDataPoint[];
  width?: number;
  height?: number;
  onClick?: () => void;
  showPointLabels?: boolean;
  isInline?: boolean;
}

const ChartContainer = styled(Box)<{ isInline?: boolean }>(({ theme, isInline }) => ({
  cursor: isInline ? 'pointer' : 'default',
  border: isInline ? '1px solid #e0e0e0' : 'none',
  borderRadius: isInline ? '4px' : '0',
  padding: isInline ? '8px' : '0',
  backgroundColor: isInline ? '#fafafa' : 'transparent',
  '&:hover': isInline ? {
    backgroundColor: '#f0f0f0',
    borderColor: "#073142",
  } : {},
}));

const ChartSvg = styled('svg')({
  width: '100%',
  height: '100%',
  overflow: 'visible',
});

export const TimeSeriesChart: FC<TimeSeriesChartProps> = ({
  historicData,
  forecastData,
  lowerConfidenceLimits80,
  upperConfidenceLimits80,
  lowerConfidenceLimits95,
  upperConfidenceLimits95,
  width = 200,
  height = 60,
  onClick,
  showPointLabels = false,
  isInline = true,
}) => {
  // Transform data to match the old app format - just numeric arrays
  const historicValues = historicData.map(d => d.value).filter(v => typeof v === 'number' && !isNaN(v));
  const forecastValues = forecastData.map(d => d.value).filter(v => typeof v === 'number' && !isNaN(v));
  
  if (historicValues.length === 0 && forecastValues.length === 0) {
    return (
      <ChartContainer isInline={isInline} onClick={onClick} data-testid="time-series-chart-container">
        <Box 
          sx={{ 
            width, 
            height, 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center',
            color: 'text.secondary',
            fontSize: '12px'
          }}
          data-testid="time-series-chart-no-data"
        >
          No data available
        </Box>
      </ChartContainer>
    );
  }

  // Calculate bounds like in the old app - only from historic and forecast data
  const allHistoricAndForecast = [...historicValues, ...forecastValues];
  const low = Math.min(...allHistoricAndForecast);
  const max = Math.max(...allHistoricAndForecast);

  // Apply limiting functions like in the old app
  const limitValueToMax = (value: number) => Math.min(value, max * 1.15);
  const limitValueToMin = (value: number) => Math.max(value, low);

  // Process confidence intervals with limits
  const upperConfidence95Values = upperConfidenceLimits95.map(d => limitValueToMax(d.value));
  const upperConfidence80Values = upperConfidenceLimits80.map(d => limitValueToMax(d.value));
  const lowerConfidence80Values = lowerConfidenceLimits80.map(d => limitValueToMin(d.value));
  const lowerConfidence95Values = lowerConfidenceLimits95.map(d => limitValueToMin(d.value));

  // Create combined labels array - this represents ALL time points including both historic and forecast
  // In the old app, confidence intervals span the entire forecast period
  const historicLabels = historicData.map(d => d.point);
  const forecastLabels = forecastData.map(d => d.point);
  const allLabels = [...historicLabels, ...forecastLabels];

  // Calculate final bounds including limited confidence intervals
  const allValues = [
    ...allHistoricAndForecast,
    ...upperConfidence95Values,
    ...lowerConfidence95Values
  ];
  const minValue = Math.min(...allValues);
  const maxValue = Math.max(...allValues);
  const valueRange = maxValue - minValue || 1;

  // Chart layout - more space for axes
  const axisSpace = isInline ? 0 : 40; // Space for axis labels and ticks
  const padding = isInline ? 10 : 20;
  const chartWidth = width - (padding * 2) - axisSpace;
  const chartHeight = height - (padding * 2) - axisSpace;
  const chartLeft = padding + (isInline ? 0 : axisSpace);
  const chartTop = padding;

  // Helper function to get X coordinate based on data index
  const getX = (index: number) => {
    const ratio = allLabels.length > 1 ? index / (allLabels.length - 1) : 0.5;
    return chartLeft + (ratio * chartWidth);
  };

  // Helper function to get Y coordinate (flipped for SVG)
  const getY = (value: number) => {
    const ratio = valueRange > 0 ? (maxValue - value) / valueRange : 0.5;
    return chartTop + (ratio * chartHeight);
  };

  // Create line path from numeric array
  const createLinePath = (values: number[], startIndex: number = 0) => {
    if (values.length === 0) return '';
    
    let path = '';
    values.forEach((value, i) => {
      const x = getX(startIndex + i);
      const y = getY(value);
      path += `${i === 0 ? 'M' : ' L'} ${x} ${y}`;
    });
    
    return path;
  };

  // Create historic line that extends to connect with first forecast point
  const createHistoricLinePath = () => {
    if (historicValues.length === 0) return '';
    
    let path = '';
    
    // Draw through all historic points
    historicValues.forEach((value, i) => {
      const x = getX(i);
      const y = getY(value);
      path += `${i === 0 ? 'M' : ' L'} ${x} ${y}`;
    });
    
    // Extend to first forecast point if it exists
    if (forecastValues.length > 0) {
      const firstForecastX = getX(historicValues.length);
      const firstForecastY = getY(forecastValues[0]);
      path += ` L ${firstForecastX} ${firstForecastY}`;
    }
    
    return path;
  };

  // Create forecast line starting from first forecast point
  const createForecastLinePath = () => {
    if (forecastValues.length === 0) return '';
    
    const forecastStartIndex = historicValues.length;
    let path = '';
    
    // Draw forecast line starting from first forecast point
    forecastValues.forEach((value, i) => {
      const x = getX(forecastStartIndex + i);
      const y = getY(value);
      path += `${i === 0 ? 'M' : ' L'} ${x} ${y}`;
    });
    
    return path;
  };

  // Create area path for confidence intervals - start from first forecast point but avoid the transition line
  const createConfidenceArea = (upperValues: number[], lowerValues: number[]) => {
    if (upperValues.length === 0 || lowerValues.length === 0) return '';

    const forecastStartIndex = historicValues.length;
    let path = '';
    
    // Start from the first forecast point (where orange line starts)
    const firstForecastX = getX(forecastStartIndex);
    const firstUpperY = getY(upperValues[0]);
    path = `M ${firstForecastX} ${firstUpperY}`;
    
    // Draw upper line through all forecast points
    for (let i = 1; i < upperValues.length; i++) {
      const x = getX(forecastStartIndex + i);
      const y = getY(upperValues[i]);
      path += ` L ${x} ${y}`;
    }
    
    // Draw lower line in reverse
    for (let i = lowerValues.length - 1; i >= 0; i--) {
      const x = getX(forecastStartIndex + i);
      const y = getY(lowerValues[i]);
      path += ` L ${x} ${y}`;
    }
    
    path += ' Z';
    return path;
  };

  // Create area under historic line
  const createHistoricArea = () => {
    if (historicValues.length === 0) return '';
    
    const linePath = createLinePath(historicValues, 0);
    const lastX = getX(historicValues.length - 1);
    const firstX = getX(0);
    const chartBottom = chartTop + chartHeight;
    
    return `${linePath} L ${lastX} ${chartBottom} L ${firstX} ${chartBottom} Z`;
  };

  const forecastStartIndex = historicValues.length;

  // Generate paths
  const confidence95Area = createConfidenceArea(upperConfidence95Values, lowerConfidence95Values);
  const confidence80Area = createConfidenceArea(upperConfidence80Values, lowerConfidence80Values);
  const historicAreaPath = createHistoricArea();
  const historicLinePath = createHistoricLinePath();
  const forecastLinePath = createForecastLinePath();

  // Generate axis tick values
  const generateYTicks = () => {
    const numTicks = isInline ? 3 : 5;
    const ticks = [];
    for (let i = 0; i <= numTicks; i++) {
      const value = minValue + (valueRange * i / numTicks);
      ticks.push(value);
    }
    return ticks;
  };

  const yTicks = generateYTicks();

  return (
    <ChartContainer isInline={isInline} onClick={onClick} data-testid="time-series-chart-container">
      <Box sx={{ width, height, position: 'relative' }} data-testid="time-series-chart-box">
        <ChartSvg width={width} height={height} viewBox={`0 0 ${width} ${height}`} data-testid="time-series-chart-svg">
          {/* Grid lines (only in modal view) */}
          {!isInline && (
            <g className="grid-group" data-testid="time-series-chart-grid-group">
              {/* Horizontal grid lines */}
              {yTicks.map((tick, index) => (
                <line
                  key={`h-grid-${index}`}
                  x1={chartLeft}
                  y1={getY(tick)}
                  x2={chartLeft + chartWidth}
                  y2={getY(tick)}
                  stroke="#e0e0e0"
                  strokeWidth="1"
                />
              ))}
              
              {/* Vertical grid lines */}
              {allLabels.map((_, index) => (
                <line
                  key={`v-grid-${index}`}
                  x1={getX(index)}
                  y1={chartTop}
                  x2={getX(index)}
                  y2={chartTop + chartHeight}
                  stroke="#e0e0e0"
                  strokeWidth="1"
                />
              ))}
            </g>
          )}

          {/* Axes (only in modal view) */}
          {!isInline && (
            <g className="axes-group" data-testid="time-series-chart-axes-group">
              {/* X-axis */}
              <line
                x1={chartLeft}
                y1={chartTop + chartHeight}
                x2={chartLeft + chartWidth}
                y2={chartTop + chartHeight}
                stroke="#333"
                strokeWidth="2"
              />
              
              {/* Y-axis */}
              <line
                x1={chartLeft}
                y1={chartTop}
                x2={chartLeft}
                y2={chartTop + chartHeight}
                stroke="#333"
                strokeWidth="2"
              />
            </g>
          )}

          {/* 95% Confidence interval (background, lightest) */}
          {confidence95Area && (
            <path
              d={confidence95Area}
              fill="rgba(158, 158, 158, 0.15)"
              stroke="none"
              data-testid="time-series-chart-confidence-95"
            />
          )}
          
          {/* 80% Confidence interval (background, darker) */}
          {confidence80Area && (
            <path
              d={confidence80Area}
              fill="rgba(158, 158, 158, 0.25)"
              stroke="none"
              data-testid="time-series-chart-confidence-80"
            />
          )}

          {/* Historic data area (background fill) */}
          {historicAreaPath && (
            <path
              d={historicAreaPath}
              fill="rgba(76, 175, 80, 0.2)"
              stroke="none"
              data-testid="time-series-chart-historic-area"
            />
          )}

          {/* Historic data line (green solid line) */}
          {historicLinePath && (
            <path
              d={historicLinePath}
              fill="none"
              stroke="#4CAF50"
              strokeWidth={isInline ? "2" : "3"}
              data-testid="time-series-chart-historic-line"
            />
          )}

          {/* Forecast data line (orange dashed line) */}
          {forecastLinePath && (
            <path
              d={forecastLinePath}
              fill="none"
              stroke="#FF9800"
              strokeWidth={isInline ? "2" : "3"}
              strokeDasharray="4,4"
              data-testid="time-series-chart-forecast-line"
            />
          )}

          {/* Historic data points */}
          {historicValues.map((value, index) => (
            <circle
              key={`historic-${index}`}
              cx={getX(index)}
              cy={getY(value)}
              r={isInline ? "2.5" : "4"}
              fill="#4CAF50"
              stroke="#fff"
              strokeWidth="1"
              data-testid={`time-series-chart-historic-point-${index}`}
            />
          ))}

          {/* Forecast data points */}
          {forecastValues.map((value, index) => (
            <circle
              key={`forecast-${index}`}
              cx={getX(forecastStartIndex + index)}
              cy={getY(value)}
              r={isInline ? "2.5" : "4"}
              fill="#FF9800"
              stroke="#fff"
              strokeWidth="1"
              data-testid={`time-series-chart-forecast-point-${index}`}
            />
          ))}

          {/* Y-axis labels (only in modal view) */}
          {!isInline && yTicks.map((tick, index) => (
            <text
              key={`y-label-${index}`}
              x={chartLeft - 10}
              y={getY(tick) + 4}
              textAnchor="end"
              fontSize="10"
              fill="#666"
              data-testid={`time-series-chart-y-label-${index}`}
            >
{tick.toFixed(1)}
            </text>
          ))}

          {/* X-axis labels (only in modal view) */}
          {!isInline && allLabels.map((label, index) => {
            // Calculate interval to show labels - aim for ~10-15 visible labels max
            const maxVisibleLabels = 15;
            const interval = allLabels.length <= maxVisibleLabels 
              ? 1 
              : Math.ceil(allLabels.length / maxVisibleLabels);
            
            // Always show first and last label, and labels at intervals
            const isFirstOrLast = index === 0 || index === allLabels.length - 1;
            const isAtInterval = index % interval === 0;
            
            if (!isFirstOrLast && !isAtInterval) return null;
            
            return (
              <text
                key={`x-label-${index}`}
                x={getX(index)}
                y={chartTop + chartHeight + 15}
                textAnchor="middle"
                fontSize="10"
                fill="#666"
                data-testid={`time-series-chart-x-label-${index}`}
              >
                {label}
              </text>
            );
          })}

          {/* Point labels (when enabled - for modal view) */}
          {showPointLabels && (
            <>
              {historicValues.map((value, index) => (
                <text
                  key={`historic-label-${index}`}
                  x={getX(index)}
                  y={getY(value) - 10}
                  textAnchor="middle"
                  fontSize="11"
                  fill="#333"
                  fontWeight="500"
                  data-testid={`time-series-chart-historic-label-${index}`}
                >
                  {value.toFixed(1)}
                </text>
              ))}
              {forecastValues.map((value, index) => (
                <text
                  key={`forecast-label-${index}`}
                  x={getX(forecastStartIndex + index)}
                  y={getY(value) - 10}
                  textAnchor="middle"
                  fontSize="11"
                  fill="#333"
                  fontWeight="500"
                  data-testid={`time-series-chart-forecast-label-${index}`}
                >
                  {value.toFixed(1)}
                </text>
              ))}
            </>
          )}
        </ChartSvg>
      </Box>
    </ChartContainer>
  );
};

export default TimeSeriesChart;
